import { useState } from "react";
import { TaskType } from "@common/types/task";
import { Drawer } from "@design-system/Drawer";
import { OverlayData, useOverlays } from "contexts/OverlayContext";
import { TaskCreationForm } from "./TaskCreationForm";
import { WorkspaceTaskPreview } from "./WorkspaceTaskPreview";
import { hasTaskCreationContext } from "./taskWorkflow";
import {
    doesSelectionViewOwnEntities,
    hasTaskSelectionPreview,
    hasTaskWorkspacePreview,
    isEntityChangeHandledByPreview,
} from "./taskCreationPreview";
import { getTaskCreationPreviewComponent } from "./taskCreationPreviewComponents";

interface WorkflowSelection {
    type: TaskType;
    changeCount: number;
}

export const CreateTaskDrawer = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo<"createTask">("createTask");

    if (!overlayInfo || !overlayInfo.data) {
        return null;
    }

    return (
        <CreateTaskDrawerContent
            data={overlayInfo.data}
            onClose={closeOverlay}
            isClosing={overlayIsClosing}
        />
    );
};

interface CreateTaskDrawerContentProps {
    data: NonNullable<OverlayData<"createTask">>;
    onClose: () => void;
    isClosing: boolean;
}

const CreateTaskDrawerContent = ({
    data,
    onClose,
    isClosing,
}: CreateTaskDrawerContentProps) => {
    const {
        type,
        viewConfig,
        transactionsTask,
        initialValues,
        relatedTaskId,
        perEntityTask,
    } = data;
    const [workflowSelection, setWorkflowSelection] =
        useState<WorkflowSelection>({ type, changeCount: 0 });

    const isPreviewShown = hasTaskSelectionPreview({
        taskType: type,
        viewConfig,
        transactions: transactionsTask?.transactions,
        transactionsCount: transactionsTask?.transactionsCount,
    });
    const isWorkspacePreviewShown = hasTaskWorkspacePreview({
        taskType: workflowSelection.type,
        hasContext: hasTaskCreationContext({
            viewConfig,
            transactionsTask,
            perEntityTask,
            relatedTaskId,
        }),
    });

    const renderPreview = () => {
        if (isPreviewShown && viewConfig) {
            const SelectionPreview = getTaskCreationPreviewComponent(type);
            if (!SelectionPreview) {
                return undefined;
            }
            return doesSelectionViewOwnEntities(type) ? (
                <SelectionPreview
                    viewConfig={viewConfig}
                    transactions={transactionsTask?.transactions}
                    isSelection
                />
            ) : (
                <WorkspaceTaskPreview
                    Preview={SelectionPreview}
                    isRemountedOnEntityChange={
                        !isEntityChangeHandledByPreview(type)
                    }
                />
            );
        }
        const WorkspacePreview = isWorkspacePreviewShown
            ? getTaskCreationPreviewComponent(workflowSelection.type)
            : undefined;
        return WorkspacePreview ? (
            <WorkspaceTaskPreview
                key={workflowSelection.changeCount}
                Preview={WorkspacePreview}
                isRemountedOnEntityChange={
                    !isEntityChangeHandledByPreview(workflowSelection.type)
                }
            />
        ) : undefined;
    };

    return (
        <Drawer
            direction="right"
            onClose={onClose}
            open={!isClosing}
            size={
                isPreviewShown || isWorkspacePreviewShown
                    ? "fullScreen"
                    : "quarter"
            }
            headerAddonLeft="Create task"
            noPadding
        >
            <TaskCreationForm
                onSuccess={onClose}
                taskType={type}
                viewConfig={viewConfig}
                transactionsTask={transactionsTask}
                initialValues={initialValues}
                relatedTaskId={relatedTaskId}
                perEntityTask={perEntityTask}
                isSelectionPreview={isPreviewShown}
                onWorkflowChange={(nextType) =>
                    setWorkflowSelection(({ changeCount }) => ({
                        type: nextType,
                        changeCount: changeCount + 1,
                    }))
                }
                preview={renderPreview()}
            />
        </Drawer>
    );
};
